import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, ModalContent, Spinner } from '@nextui-org/react';
import { MapPin, Search, SearchX, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import searchService, {
  GroupedSearchResults,
  SearchResult,
  SearchResultType,
} from '../services/searchService';
import nearbyService, {
  formatDistance,
  GeoPoint,
  NearbyItem,
} from '../services/nearbyService';
import userService from '../services/userService';
import SearchDiscoverContent from '../components/search/SearchDiscoverContent';
import { useAuth } from './AuthContext';
import { t as tr } from '../i18n';

// ============================================
// SearchExplorer – Port des Flutter SearchExplorer Modals
//
// Schnellsuche von überall in der App. Öffnet als zentriertes Modal.
// Wird via useSearchExplorer().open() geöffnet (Header-Suchicon, Hero,
// sowie global per ⌘K / Strg+K).
// ============================================

const ACCENT = '#3fced1';
const DEBOUNCE_MS = 300;

// Reihenfolge & Emoji-Labels (zentrale Suche 2026-09-01: alle Typen).
// PDC-Turniere GANZ oben — danach wird am meisten gesucht (Marcel).
const EXPLORER_ORDER: SearchResultType[] = [
  'proTournament',
  'proPlayer',
  'player',
  'tournament',
  'series',
  'club',
  'pub',
  'shop',
  'league',
  'association',
  'news',
  'forum',
  'prediction',
  'knowledge',
  'help',
];

const EXPLORER_LABELS: Record<SearchResultType, string> = {
  proPlayer: tr('web.context.search-explorer-context.profis'),
  player: tr('web.context.search-explorer-context.spieler'),
  tournament: tr('web.context.search-explorer-context.turniere'),
  series: tr('web.context.search-explorer-context.turnierserien'),
  club: tr('web.context.search-explorer-context.vereine'),
  pub: tr('web.context.search-explorer-context.spielstaetten'),
  shop: tr('web.context.search-explorer-context.dart-shops'),
  league: tr('web.context.search-explorer-context.ligen'),
  association: tr('web.context.search-explorer-context.verbaende'),
  news: tr('web.context.search-explorer-context.news'),
  forum: tr('web.context.search-explorer-context.forum'),
  prediction: tr('web.context.search-explorer-context.tippspiele'),
  knowledge: tr('web.context.search-explorer-context.darts-wissen'),
  help: tr('web.context.search-explorer-context.hilfe'),
  proTournament: tr('web.context.search-explorer-context.pdc-turniere'),
};

interface SearchExplorerContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const SearchExplorerContext = createContext<SearchExplorerContextValue | null>(null);

export const useSearchExplorer = (): SearchExplorerContextValue => {
  const ctx = useContext(SearchExplorerContext);
  if (!ctx) {
    throw new Error(tr('web.context.search-explorer-context.usesearchexplorer-muss-innerhalb-des-sea'));
  }
  return ctx;
};

export const SearchExplorerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Globaler Shortcut ⌘K / Strg+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <SearchExplorerContext.Provider value={value}>
      {children}
      <SearchExplorerModal isOpen={isOpen} onClose={close} />
    </SearchExplorerContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const SearchExplorerModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<GroupedSearchResults>(searchService.emptyResults());
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ---------- "In deiner Nähe" (wie NearbyPage: Profil-Adresse > Browser-Standort) ----------
  const profileLocation = useMemo<GeoPoint | null>(
    () => nearbyService.getProfileLocation(user),
    [user],
  );
  const [browserLocation, setBrowserLocation] = useState<GeoPoint | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const nearbyLocation = browserLocation ?? profileLocation;

  const [nearbyItems, setNearbyItems] = useState<NearbyItem[] | null>(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const nearbyLocKeyRef = useRef<string | null>(null);

  // "In der Nähe von <Suchbegriff>" – Geocoding des Suchbegriffs (wie Flutter-Turniersuche)
  const [queryNearby, setQueryNearby] = useState<{ label: string; items: NearbyItem[] } | null>(
    null,
  );
  const [queryNearbyLoading, setQueryNearbyLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !nearbyLocation) return;
    const key = `${nearbyLocation.latitude},${nearbyLocation.longitude}`;
    if (nearbyLocKeyRef.current === key) return;
    nearbyLocKeyRef.current = key;
    let stale = false;
    setNearbyLoading(true);
    nearbyService
      .loadNearbyItems(nearbyLocation, { radiusKm: 100, limit: 8 })
      .then((items) => {
        if (!stale) setNearbyItems(items);
      })
      .catch(() => {
        if (!stale) setNearbyItems([]);
      })
      .finally(() => {
        if (!stale) setNearbyLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [isOpen, nearbyLocation]);

  const requestBrowserLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(tr('web.context.search-explorer-context.standortbestimmung-wird-von-diesem-brows'));
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoading(false);
        setBrowserLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          label: tr('web.context.search-explorer-context.mein-standort'),
        });
      },
      () => {
        setGeoLoading(false);
        setGeoError(tr('web.context.search-explorer-context.standort-konnte-nicht-ermittelt-werden'));
      },
      { timeout: 10000 },
    );
  };

  // Beim Öffnen zurücksetzen und fokussieren
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebounced('');
      setResults(searchService.emptyResults());
      setHasSearched(false);
      setLoading(false);
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);

  // Debounce
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  // Suche ausführen
  useEffect(() => {
    if (!isOpen) return;
    if (debounced.length < searchService.minQueryLength) {
      requestIdRef.current += 1;
      setLoading(false);
      setResults(searchService.emptyResults());
      setHasSearched(false);
      setQueryNearby(null);
      setQueryNearbyLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    searchService
      .searchAll(debounced)
      .then((grouped) => {
        if (requestId !== requestIdRef.current) return;
        setResults(grouped);
        setHasSearched(true);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setResults(searchService.emptyResults());
        setHasSearched(true);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });

    // Parallel: Suchbegriff geocoden (z.B. "Melle") und Umkreis-Ergebnisse laden
    setQueryNearby(null);
    setQueryNearbyLoading(true);
    userService
      .geocodeAddress(debounced)
      .then(async (geos) => {
        if (requestId !== requestIdRef.current) return;
        const geo = Array.isArray(geos)
          ? geos.find(
              (g) =>
                typeof g?.latitude === 'number' &&
                isFinite(g.latitude) &&
                typeof g?.longitude === 'number' &&
                isFinite(g.longitude),
            )
          : undefined;
        if (!geo) return;
        // Mehr Kandidaten laden als angezeigt werden: die nächstgelegenen sind oft
        // identisch mit den normalen Suchtreffern und fallen der Deduplizierung zum Opfer
        const items = await nearbyService.loadNearbyItems(
          { latitude: geo.latitude as number, longitude: geo.longitude as number },
          { radiusKm: 50, limit: 24 },
        );
        if (requestId !== requestIdRef.current) return;
        setQueryNearby({
          label:
            typeof geo.formattedAddress === 'string' && geo.formattedAddress.trim()
              ? geo.formattedAddress.trim()
              : debounced,
          items,
        });
      })
      .catch(() => {
        /* Geocoding-Fehler still ignorieren – normale Suche bleibt unberührt */
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setQueryNearbyLoading(false);
      });
  }, [debounced, isOpen]);

  const totalCount = useMemo(() => searchService.countResults(results), [results]);

  // Umkreis-Treffer, die bereits in den normalen Ergebnissen stehen, ausblenden
  const queryNearbyItems = useMemo<NearbyItem[]>(() => {
    if (!queryNearby) return [];
    const knownLinks = new Set<string>();
    (Object.values(results) as SearchResult[][]).forEach((list) => {
      (list || []).forEach((r) => {
        if (r?.link) knownLinks.add(r.link);
      });
    });
    return queryNearby.items
      .filter((item) => !item.link || !knownLinks.has(item.link))
      .slice(0, 6);
  }, [queryNearby, results]);

  const handleResultClick = (result: SearchResult) => {
    if (result.link) {
      // Erfolgreiche Suche → in die Historie ("Zuletzt gesucht")
      searchService.addRecentSearch(debounced);
      onClose();
      navigate(result.link);
    }
  };

  // Mobile: Vollbild-Explorer statt schwebendem Modal
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Tastatur-Fix (iOS Safari): 100dvh reagiert nicht auf die Bildschirm-
  // Tastatur — der Viewport wird verschoben statt verkleinert und die
  // Suchleiste verschwindet nach oben. Deshalb rendert Mobile ein EIGENES
  // fixed-Overlay (kein NextUI-Modal, dessen Scroll-Wrapper Safari mitzieht)
  // und folgt der visualViewport-API: echte sichtbare Höhe + Offset.
  const [vvStyle, setVvStyle] = useState<React.CSSProperties | undefined>(undefined);
  useEffect(() => {
    if (!isOpen || !isMobileViewport) {
      setVvStyle(undefined);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      // Safari scrollt die Seite hinter dem Overlay hoch — zurückdrehen,
      // damit offsetTop klein bleibt und das Overlay exakt sitzt.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      setVvStyle({
        height: `${Math.round(vv.height)}px`,
        transform: tr('web.context.search-explorer-context.translatey-px', { round: Math.round(vv.offsetTop) }),
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setVvStyle(undefined);
    };
  }, [isOpen, isMobileViewport]);

  // Body-Scroll-Lock fürs eigene Mobile-Overlay (NextUI übernimmt das sonst)
  useEffect(() => {
    if (!isOpen || !isMobileViewport) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isMobileViewport]);

  const renderBody = (onCloseInner: () => void) => (
          <div
            className={isMobileViewport ? 'flex h-full flex-col overflow-hidden' : 'contents'}
          >
            {/* Header mit Suchfeld */}
            <div className="flex shrink-0 items-center gap-3 p-4 sm:p-5">
              <Search size={22} className="shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tr('web.context.search-explorer-context.news-turniere-vereine-spielstaetten-such')}
                className="flex-1 bg-transparent text-base text-white placeholder:text-gray-500 outline-none border-none"
              />
              <button
                type="button"
                aria-label={tr('web.context.search-explorer-context.suche-schliessen')}
                onClick={onCloseInner}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-gray-300 hover:bg-zinc-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="h-px bg-zinc-800" />

            {/* Ergebnisse */}
            <div
              className={`${
                isMobileViewport ? 'flex-1' : 'max-h-[60vh]'
              } overflow-y-auto p-4 sm:p-5`}
            >
              {/* Leerer Zustand: Discover-Slider (Zuletzt gesucht, Profis,
                  News, Turniere, Nearby, Neue Spieler) */}
              {!loading && debounced.length < searchService.minQueryLength && (
                <SearchDiscoverContent
                  onNavigate={(link) => {
                    onCloseInner();
                    navigate(link);
                  }}
                  onSearch={(term) => setQuery(term)}
                  nearbyLocation={nearbyLocation}
                  nearbyItems={nearbyItems}
                  nearbyLoading={nearbyLoading}
                  geoLoading={geoLoading}
                  geoError={geoError}
                  onRequestLocation={requestBrowserLocation}
                />
              )}

              {/* Laden */}
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Spinner color="primary" />
                </div>
              )}

              {/* Keine Treffer (weder Suche noch Umkreis) */}
              {!loading &&
                hasSearched &&
                totalCount === 0 &&
                !queryNearbyLoading &&
                queryNearbyItems.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <SearchX size={64} className="mb-4 text-zinc-700" />
                    <p className="text-base text-gray-500">{tr('web.context.search-explorer-context.keine-ergebnisse-fuer', { debounced })}</p>
                  </div>
                )}

              {/* Gruppierte Treffer + Umkreis-Sektion */}
              {!loading && hasSearched && (totalCount > 0 || queryNearbyItems.length > 0) && (
                <div className="space-y-6">
                  {EXPLORER_ORDER.map((type) => {
                    const items = results[type] || [];
                    if (items.length === 0) return null;
                    return (
                      <section key={type}>
                        <h3 className="mb-3 text-sm font-semibold text-gray-400">
                          {EXPLORER_LABELS[type]} ({items.length})
                        </h3>
                        <div className="space-y-2">
                          {items.map((result) => (
                            <SearchResultRow
                              key={`${result.type}-${result.id}`}
                              result={result}
                              onClick={() => handleResultClick(result)}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })}

                  {/* Umkreis-Ergebnisse zum geocodeten Suchbegriff (z.B. "Melle") */}
                  {queryNearbyItems.length > 0 && queryNearby && (
                    <section>
                      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                        <MapPin size={15} style={{ color: ACCENT }} className="shrink-0" />
                        <span className="min-w-0 truncate">
                          {tr('web.context.search-explorer-context.in-der-naehe-von', { label: queryNearby.label })}
                        </span>
                      </h3>
                      <div className="space-y-2">
                        {queryNearbyItems.map((item) => (
                          <NearbyResultRow
                            key={`qn-${item.type}-${item.id}`}
                            item={item}
                            onClick={() => {
                              if (item.link) {
                                onCloseInner();
                                navigate(item.link);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
  );

  // Mobile: eigenes Fullscreen-Overlay (fixed über allem, visualViewport-Höhe)
  if (isMobileViewport) {
    if (!isOpen) return null;
    return (
      <div
        className="fixed inset-x-0 top-0 z-[1000] bg-zinc-900"
        style={vvStyle ?? { height: '100dvh' }}
      >
        {renderBody(onClose)}
      </div>
    );
  }

  // Desktop: schwebendes NextUI-Modal wie bisher
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      size="2xl"
      scrollBehavior="inside"
      backdrop="blur"
      placement="top"
      classNames={{
        base: tr('web.context.search-explorer-context.bg-zinc-900-border-border'),
        backdrop: 'bg-black/60',
      }}
    >
      <ModalContent>{renderBody}</ModalContent>
    </Modal>
  );
};

const NearbyResultRow: React.FC<{ item: NearbyItem; onClick: () => void }> = ({
  item,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = item.image && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.link}
      className="flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-800/40 p-3 text-left transition-colors hover:bg-zinc-800 disabled:cursor-default"
    >
      {showImage ? (
        <img
          src={item.image as string}
          alt={item.name || ''}
          onError={() => setImgError(true)}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-zinc-800"
          style={{ color: ACCENT }}
        >
          <MapPin size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-semibold text-white">{item.name || '–'}</p>
        <p className="line-clamp-1 text-xs text-gray-400">
          <span className="text-gray-500">{item.typeLabel}</span>
          {item.subtitle ? ` · ${item.subtitle}` : ''}
        </p>
      </div>
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums"
        style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
      >
        {formatDistance(item.distance)}
      </span>
    </button>
  );
};

const SearchResultRow: React.FC<{ result: SearchResult; onClick: () => void }> = ({
  result,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = result.imageUrl && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!result.link}
      className="flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-800/40 p-3 text-left transition-colors hover:bg-zinc-800 disabled:cursor-default"
    >
      {showImage ? (
        <img
          src={result.imageUrl as string}
          alt={result.title}
          onError={() => setImgError(true)}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-zinc-800"
          style={{ color: ACCENT }}
        >
          <Search size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-semibold text-white">{result.title}</p>
        {result.subtitle && (
          <p className="line-clamp-1 text-xs text-gray-400">{result.subtitle}</p>
        )}
      </div>
    </button>
  );
};

export default SearchExplorerProvider;
